import React from "react";
import { useTranslation } from "react-i18next";
import { AvForm } from "availity-reactstrap-validation";
import { Modal } from "reactstrap";
import { useSnapshot } from "valtio";
import { useSelector, useDispatch } from "react-redux";

import { store } from "@components/VerticalLayout/store";
import { getUuidUser } from "@helpers/getUuidUser";
import { CESAR_LOYALTY, FIDELITY_POINTS_BLOCK_SIZE } from "@constants/index";

import { useFetchGlobal } from "../../../api/hooks";

import { RootState, AppDispatch } from "../../../redux/store";
import { setIsConsultModalOpened } from "../../../redux/slices/fidelitySlice";
import { handleValidate } from "./helpers";
import { useFidelityForm } from "./useFidelityForm";
import { ScrollableCardBody } from "./InformationFidelityParts/styled";
import { DesignationField } from "./InformationFidelityParts/DesignationField";
import { LoyaltySelectField } from "./InformationFidelityParts/LoyaltySelectField";
import { FidelityProviderSection } from "./InformationFidelityParts/FidelityProviderSection";
import { RemarkField } from "./InformationFidelityParts/RemarkField";
import { FormHeader } from "./InformationFidelityParts/FormHeader";
import { FormFooter } from "./InformationFidelityParts/FormFooter";

import "@assets/swal.css";
import "@assets/theme.css";
import "react-toastify/dist/ReactToastify.css";
import "./index.css";

type FidelityFormType = {
    selectedShopsRows: FidelityRow[];
    setGlobalDataIsUpdated: React.Dispatch<
        React.SetStateAction<boolean | number>
    >;
};

export default function InformationFidelity({
    selectedShopsRows,
    setGlobalDataIsUpdated,
}: FidelityFormType) {
    const { fetchGlobal } = useFetchGlobal();
    const { t } = useTranslation();
    const isConsultModalOpened = useSelector(
        (s: RootState) => s.fidelity.isConsultModalOpened
    );
    const dispatch = useDispatch<AppDispatch>();
    const { userID, franchiseID, operatorID } = useSnapshot(store);
    const uuidUser = getUuidUser();

    const form = useFidelityForm(selectedShopsRows, t);
    const { values, flags, setFlag, setError } = form;

    const setIsEdited = (v: boolean) => setFlag("isEdited", v);

    const isFidelityNumberInValid =
        values.numberFidelityPoints !== "" &&
        Number(values.numberFidelityPoints) % FIDELITY_POINTS_BLOCK_SIZE !== 0;

    const fidelityChoiceIndex = selectedShopsRows[0].fidelityChoiceIndex;
    const showRemark = fidelityChoiceIndex !== CESAR_LOYALTY;

    const closeModal = () =>
        dispatch(setIsConsultModalOpened(!isConsultModalOpened));
    const cancel = () => dispatch(setIsConsultModalOpened(false));
    const validate = () =>
        handleValidate({
            selectedShopsRows,
            values,
            flags,
            setFlag,
            setError,
            userID,
            franchiseID,
            operatorID,
            uuidUser,
            fetchGlobal,
            t,
        });

    return (
        <React.Fragment>
            <Modal
                centered
                isOpen={isConsultModalOpened}
                className="information_fidelity_modal__clz mt-5"
            >
                <AvForm className="card information_fidelity__clz">
                    <FormHeader onClose={closeModal} />
                    <ScrollableCardBody className="pl-3 pr-5">
                        <DesignationField
                            form={form}
                            setIsEdited={setIsEdited}
                        />
                        <LoyaltySelectField
                            choiceFidelity={values.choiceFidelity}
                        />
                        <FidelityProviderSection
                            form={form}
                            fidelityChoiceIndex={fidelityChoiceIndex}
                            isFidelityNumberInValid={isFidelityNumberInValid}
                            setIsEdited={setIsEdited}
                        />
                        {showRemark ? (
                            <RemarkField
                                form={form}
                                setIsEdited={setIsEdited}
                                isConsultModalOpened={isConsultModalOpened}
                            />
                        ) : null}
                    </ScrollableCardBody>
                    <FormFooter
                        isCentralizationActive={flags.isCentralizationActive}
                        onCancel={cancel}
                        onValidate={validate}
                    />
                </AvForm>
            </Modal>
        </React.Fragment>
    );
}
